import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";
import { RichText } from "@/components/RichText";
import SignActions from "@/components/landing/SignActions";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const COLORS = ["bg-brand-teal/10 text-brand-teal dark:bg-brand-teal/25 dark:text-teal-300", "bg-brand-red/10 text-brand-red dark:bg-brand-red/25 dark:text-red-300", "bg-brand-yellow/15 text-brand-yellow dark:bg-brand-yellow/25 dark:text-yellow-300", "bg-brand-navy/10 text-brand-navy dark:bg-white/15 dark:text-white"];

const GOV_SIGN_URL = "https://www.gov.pl/web/gov/podpisz-dokument-elektronicznie-wykorzystaj-podpis-zaufany";
const GOV_PHRASE = "Podpisz dokument elektronicznie";
const linkifyGov = (text) => {
  const idx = text.indexOf(GOV_PHRASE);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <a data-testid="documents-guide-gov-inline-link" href={GOV_SIGN_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-teal hover:text-brand-tealDark underline underline-offset-2">{GOV_PHRASE}</a>
      {text.slice(idx + GOV_PHRASE.length)}
    </>
  );
};

export default function Documents() {
  const { t, lang } = useT();
  useSeo({ title: "Generator dokumentów dla klientów wirtualnego biura", description: "Bezpłatny generator dokumentów wirtualnego biura w Warszawie: upoważnienie do odbioru korespondencji, zmiana danych, wypowiedzenie. Wypełnij online, pobierz PDF, podpisz profilem zaufanym.", path: ROUTES.docs, breadcrumbs: [{ name: "Dokumenty", path: ROUTES.docs }] });
  const [docs, setDocs] = useState(null);
  const [faq, setFaq] = useState(null);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
    api.get("/documents/faq").then((r) => setFaq(r.data)).catch(() => setFaq(null));
    if (window.location.hash === "#biuro-wirtualne-poradnik") setTimeout(() => document.getElementById("biuro-wirtualne-poradnik")?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [t.docs.title]);

  const pl = lang === "pl";
  const faqTitle = faq ? (pl ? faq.title : faq.title_en || faq.title) : t.docs.faq_title;
  const faqItems = faq ? faq.items.map((it) => ({ q: pl ? it.q : it.q_en || it.q, a: pl ? it.a : it.a_en || it.a })) : t.docs.faq;

  return (
    <div data-testid="documents-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to="/" data-testid="documents-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.footer.back_home}
          </Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">
          <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-yellow mr-3 align-middle" />{t.docs.title}
        </p>
        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl mt-4 max-w-2xl">{t.docs.list_title}</h1>
        <p className="text-base text-muted-foreground mt-4 max-w-2xl leading-relaxed">{t.docs.list_sub}</p>
        <p data-testid="documents-sign-note" className="text-base text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          {t.docs.sign_note}{" "}
          <a data-testid="documents-sign-email" href="mailto:biuro@twojebiuro.waw.pl?subject=Podpisany%20dokument" className="font-semibold text-brand-teal hover:text-brand-tealDark underline underline-offset-4 transition-colors">
            biuro@twojebiuro.waw.pl
          </a>{" "}
          {t.docs.sign_or_here}{" "}
          <Link to={ROUTES.upload} data-testid="documents-sign-upload-link" className="font-semibold text-brand-teal hover:text-brand-tealDark underline underline-offset-4 transition-colors">
            {t.docs.guide_link_mail}
          </Link>.
        </p>
        <div id="wyslij" data-testid="documents-upload" className="scroll-mt-24 max-w-2xl"><SignActions /></div>
        {!docs ? (
          <Loader2 className="animate-spin text-slate-500 mt-12" size={28} />
        ) : (
          <div data-testid="documents-list" className="mt-12 grid sm:grid-cols-2 gap-4">
            {docs.map((d, i) => (
              <Link
                key={d.id}
                to={`/biuro-wirtualne-warszawa/dokumenty/${d.id}`}
                data-testid={`documents-item-${d.id}`}
                className="group bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl p-6 flex items-start gap-4 hover:border-brand-teal/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${COLORS[i % COLORS.length]}`}><FileText size={20} /></div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-bold text-base md:text-lg leading-snug">{d.name}</h2>
                  {d.description && <p data-testid={`documents-desc-${d.id}`} className="text-sm text-muted-foreground mt-2 leading-relaxed">{d.description}</p>}
                  <p className="text-xs text-muted-foreground/80 mt-2">{t.docs.fields_count.replace("{n}", d.fields.length)}</p>
                </div>
                <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-brand-teal transition-colors shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        )}
        <section id="biuro-wirtualne-poradnik" data-testid="documents-sign-guide" className="scroll-mt-24 mt-14 bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">
            <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-teal mr-3 align-middle" />{t.docs.guide_overline}
          </p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-3">{t.docs.guide_title}</h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl leading-relaxed">{t.docs.guide_sub}</p>
          <ol className="mt-8 grid md:grid-cols-2 gap-5">
            {t.docs.guide_steps.map((step, i) => (
              <li key={i} data-testid={`documents-guide-step-${i + 1}`} className="flex gap-4">
                <span className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center font-display font-bold text-sm ${["bg-brand-teal text-white", "bg-brand-yellow text-brand-navy", "bg-brand-red text-white", "bg-brand-navy text-white dark:bg-white dark:text-brand-navy", "bg-brand-teal text-white", "bg-brand-yellow text-brand-navy"][i % 6]}`}>{i + 1}</span>
                <div>
                  <h3 className="font-semibold text-sm">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{linkifyGov(step.text)}</p>
                  {i === t.docs.guide_steps.length - 1 && (
                    <Link to={ROUTES.upload} data-testid="documents-guide-step-upload-link" className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
                      {t.docs.guide_link_mail} <ArrowUpRight size={14} />
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex flex-wrap gap-3">
            <a data-testid="documents-guide-link-gov" href="https://www.gov.pl/web/gov/podpisz-dokument-elektronicznie-wykorzystaj-podpis-zaufany" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:border-brand-teal hover:text-brand-teal transition-colors">
              {t.docs.guide_link_gov} <ArrowUpRight size={14} />
            </a>
            <a data-testid="documents-guide-link-mail" href="/biuro-wirtualne-warszawa/wyslij-podpisany-dokument" className="inline-flex items-center gap-2 text-xs font-semibold bg-brand-teal text-white rounded-full px-4 py-2 hover:bg-brand-tealDark transition-colors">
              {t.docs.guide_link_mail} <ArrowUpRight size={14} />
            </a>
          </div>
        </section>
        <section data-testid="documents-faq" className="mt-10 bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">
            <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />FAQ
          </p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-3">{faqTitle}</h2>
          <Accordion type="single" collapsible className="mt-6">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`documents-faq-item-${i + 1}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold hover:text-brand-teal hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed"><RichText text={item.a} /></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <a data-testid="documents-faq-guide-link" href="#biuro-wirtualne-poradnik" onClick={(e) => { e.preventDefault(); document.getElementById("biuro-wirtualne-poradnik")?.scrollIntoView({ behavior: "smooth" }); }} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
            {t.docs.guide_cta} <ArrowUpRight size={15} />
          </a>
        </section>
        <p className="text-xs text-muted-foreground mt-10 leading-relaxed max-w-2xl">{t.docs.note}</p>
      </main>
    </div>
  );
}
